"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

const STORAGE_KEY = "praetor:isDebugMode";

// Missing env var → treat as production (gate closed). Devs must set to "false" explicitly.
const IS_PRODUCTION = import.meta.env.VITE_IS_PRODUCTION !== "false";

type UserSettings = {
  isDebugMode: boolean;
  isProductionEnvironment: boolean;
};

type UserSettingsContextValue = {
  settings: UserSettings;
  isLoading: boolean;
  setDebugMode: (enabled: boolean) => void;
  toggleDebugMode: () => void;
  resetSettings: () => void;
};

const DEFAULT_SETTINGS: UserSettings = {
  isDebugMode: false,
  isProductionEnvironment: IS_PRODUCTION,
};

const UserSettingsContext = createContext<UserSettingsContextValue>({
  settings: DEFAULT_SETTINGS,
  isLoading: true,
  setDebugMode: () => {},
  toggleDebugMode: () => {},
  resetSettings: () => {},
});

function readFromStorage(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === "true";
}

function writeToStorage(enabled: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, String(enabled));
}

export function UserSettingsProvider({ children }: { children: ReactNode }) {
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setIsDebugMode(readFromStorage());
    setMounted(true);
  }, []);

  const setDebugMode = useCallback((enabled: boolean) => {
    setIsDebugMode(enabled);
    writeToStorage(enabled);
  }, []);

  const toggleDebugMode = useCallback(() => {
    setIsDebugMode((prev) => {
      const next = !prev;
      writeToStorage(next);
      return next;
    });
  }, []);

  const resetSettings = useCallback(() => {
    setIsDebugMode(false);
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const settings: UserSettings = {
    isDebugMode: mounted ? isDebugMode : false,
    isProductionEnvironment: IS_PRODUCTION,
  };

  return (
    <UserSettingsContext.Provider
      value={{
        settings,
        isLoading: !mounted,
        setDebugMode,
        toggleDebugMode,
        resetSettings,
      }}
    >
      {children}
    </UserSettingsContext.Provider>
  );
}

export function useUserSettings() {
  return useContext(UserSettingsContext);
}

/**
 * Debug mode is forced off in Production regardless of localStorage.
 * Consumers don't need to check the environment themselves.
 */
export function useDebugMode() {
  const { settings, setDebugMode, toggleDebugMode } = useUserSettings();
  const debugMode = !settings.isProductionEnvironment && settings.isDebugMode;
  return { debugMode, setDebugMode, toggleDebugMode };
}
